import React, { useMemo } from 'react'
import moment from 'moment'
import clsx from 'clsx'

import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'

import {
  useUsersQuery,
  useDisclaimerLogQuery,
  useReportQuery,
  useDownloadCsv,
} from '../../services/stream-manager'
import { withAdmin } from '../../services/msal'

import usePaginatedFilteredQuery from '../../hooks/usePaginatedFilteredQuery'

import EnhancedTable from '../table/EnhancedTable'

const disclaimerColumns = [
  {
    Header: 'Name',
    accessor: 'name',
  },
  {
    id: 'accepted',
    Header: 'Accepted',
    accessor: 'disclaimer_acknowledged',
    Cell: ({ value }) => (value ? 'Yes' : 'No'),
  },
  {
    id: 'date',
    Header: 'Date',
    accessor: 'disclaimer_acknowledged',
    Cell: ({ value }) => (value ? moment.utc(value).format('D MMMM YYYY') : ''),
  },
  {
    id: 'time',
    Header: 'Time',
    accessor: 'disclaimer_acknowledged',
    Cell: ({ value }) => (value ? moment.utc(value).format('HH:mm z') : ''),
  },
]

const downloadColumns = [
  {
    Header: 'Stream',
    accessor: 'target_name',
  },
  {
    Header: 'Downloaded by',
    accessor: 'username',
  },
  {
    Header: 'Time range',
    accessor: 'new_value',
  },
  {
    id: 'date',
    Header: 'Date',
    accessor: 'date',
    Cell: ({ value }) => moment.utc(value).format('D MMMM YYYY'),
  },
  {
    id: 'time',
    Header: 'Time',
    accessor: 'date',
    Cell: ({ value }) => moment.utc(value).format('HH:mm z'),
  },
]

const useStyles = makeStyles((theme) => ({
  code: {
    fontSize: 'smaller',
    fontFamily: 'monospace',
    whiteSpace: 'pre',
  },
  added: {
    color: theme.palette.success.main,
  },
  updated: {
    color: theme.palette.warning.main,
  },
  deleted: {
    color: theme.palette.error.main,
  },
}))

const Logs = () => {
  const classes = useStyles()

  const settingsColumns = useMemo(
    () => [
      {
        Header: 'Target',
        accessor: 'target_name',
      },
      {
        Header: 'Table',
        accessor: 'table',
        Cell: ({ value }) => <span className={classes.code}>{value}</span>,
      },
      {
        Header: 'Operation',
        accessor: 'operation',
        Cell: ({ value }) => (
          <span
            className={clsx(classes.code, {
              [classes.added]: value === 'add',
              [classes.updated]: value === 'update',
              [classes.deleted]: value === 'delete',
            })}
          >
            {value}
          </span>
        ),
      },
      {
        Header: 'Field',
        accessor: 'field',
        Cell: ({ value }) => <span className={classes.code}>{value}</span>,
      },
      {
        Header: 'New value',
        accessor: 'new_value',
        Cell: ({ value }) => <span className={classes.added}>{value}</span>,
      },
      {
        Header: 'Previous value',
        accessor: 'previous_value',
        Cell: ({ value }) => <span className={classes.deleted}>{value}</span>,
      },
      {
        Header: 'Done by',
        accessor: 'username',
      },
      {
        id: 'date',
        Header: 'Date',
        accessor: 'date',
        Cell: ({ value }) => moment.utc(value).format('D MMMM YYYY'),
      },
      {
        id: 'time',
        Header: 'Time',
        accessor: 'date',
        Cell: ({ value }) => moment.utc(value).format('HH:mm z'),
      },
    ],
    [classes],
  )

  return (
    <>
      <Paper>
        <EnhancedTable
          label="Disclaimer Acceptance Log"
          columns={disclaimerColumns}
          itemsQuery={usePaginatedFilteredQuery(useUsersQuery, {
            urlKey: 'disclaimer',
          })({ params: { order: '-disclaimer_acknowledged' } })}
          downloadCsv={useDownloadCsv(
            usePaginatedFilteredQuery(useDisclaimerLogQuery, {
              urlKey: 'disclaimer',
            }),
          )()}
          search={{ dateTime: true }}
          urlKey="disclaimer"
        />
      </Paper>
      <br />
      <Paper>
        <EnhancedTable
          label="Downloaded Content Log"
          columns={downloadColumns}
          itemsQuery={usePaginatedFilteredQuery(useReportQuery, {
            urlKey: 'downloads',
          })({ params: { report_type: "download_audit" } })}
          downloadCsv={useDownloadCsv(
            usePaginatedFilteredQuery(useReportQuery, { urlKey: 'downloads' }),
          )({ params: { report_type: "download_audit" } })}
          search={{ dateTime: true }}
          urlKey="downloads"
        />
      </Paper>
      <br />
      <Paper>
        <EnhancedTable
          label="Setting Changes Log"
          columns={settingsColumns}
          itemsQuery={usePaginatedFilteredQuery(useReportQuery, {
            urlKey: 'settings',
          })({ params: { report_type: "settings_audit" } })}
          downloadCsv={useDownloadCsv(
            usePaginatedFilteredQuery(useReportQuery, { urlKey: 'settings' }),
          )({ params: { report_type: "settings_audit" } })}
          search={{ dateTime: true }}
          urlKey="settings"
        />
      </Paper>
    </>
  )
}

export const LogsPage = withAdmin(Logs)
